import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu } from '@angular/material/menu';
import { PaymentService } from '@galaxy/billing';
import { FeatureFlagService } from '@galaxy/partner';
import { ConversationChannel, MediaInterface } from '@vendasta/conversation';
import {
  ChatComposerComponent,
  ChatSource,
  ChatSourceId,
  SentMessage,
} from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FileInfo, FileUploadStatus, GALAXY_UPLOADER_SERVICE_TOKEN } from '@vendasta/galaxy/uploader';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  first,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { ConversationService } from '../../../../../../core/src/lib/conversation.service';
import { isMobile } from '../../../../../../core/src/lib/inbox-utils';
import { InboxService } from '../../../../../../core/src/lib/inbox.service';
import {
  ConversationAvailableChannels,
  ConversationDetail,
  MessageInfo,
} from '../../../../../../core/src/lib/interface/conversation.interface';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  PARTNER_ID_TOKEN,
} from '../../../../../../core/src/lib/tokens';
import { InboxInsertPaymentLinkComponent } from '../inbox-insert-payment-link/inbox-insert-payment-link.component';
import { InboxInsertReviewLinkComponent } from '../inbox-insert-review-link/inbox-insert-review-link.component';
import { InboxInsertTemplateComponent } from '../inbox-insert-template/inbox-insert-template.component';
import { InboxUploadFileComponent } from '../inbox-upload-file/inbox-upload-file.component';
import { InboxUploadService } from '../inbox-upload-file/inbox-upload.service';

export interface ChatSourceInfo {
  channel: ConversationChannel;
  phoneNumber?: string;
}

interface PopoverConfig {
  title: string;
  contentFirst: string;
  contentSecond: string;
}

type FileTypes = 'image' | 'other';

interface ComposerAction {
  id: string;
  action: string;
  icon: string;
  onClick: () => void;
  matMenu?: MatMenu | null;
  visible: Observable<boolean>;
}

@Component({
  selector: 'inbox-message-text',
  templateUrl: './inbox-message-text.component.html',
  styleUrls: ['./inbox-message-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useExisting: InboxUploadService }],
})
export class InboxMessageTextComponent implements OnChanges, AfterViewInit {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly snackbarService = inject(SnackbarService);

  @ViewChild('chatComposer') chatComposer?: ChatComposerComponent;
  @ViewChild('inboxUploadFile') inboxUploadFile?: InboxUploadFileComponent;
  @ViewChild('inboxInsertReviewLink') inboxInsertReview?: InboxInsertReviewLinkComponent;
  @ViewChild('inboxInsertPaymentLink') inboxInsertPaymentLink?: InboxInsertPaymentLinkComponent;

  @Input() isImpersonating = false;
  @Input() clearMessageText?: boolean;
  @Input() prefilledMessage?: string;
  @Input() missingContactInformation = false;

  private readonly availableChannels$$ = new ReplaySubject<ConversationAvailableChannels>(1);
  readonly availableChannels$ = this.availableChannels$$.asObservable();

  @Input({ required: true }) set availableChannels(value: ConversationAvailableChannels | null) {
    if (value) {
      this.availableChannels$$.next(value);
      if (value.preferredChannel !== ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED) {
        this.selectedChatSourceId.set(mapConversationChannelToChatSource(value.preferredChannel).id);
      }
    }
  }

  private readonly conversationDetail$$ = new BehaviorSubject<ConversationDetail | null>(null);
  readonly conversationDetail$ = this.conversationDetail$$.asObservable();

  @Input() set conversationDetail(value: ConversationDetail | null) {
    this.conversationDetail$$.next(value);
  }

  @Output() sendMessage = new EventEmitter<MessageInfo>();
  @Output() fileChange = new EventEmitter<FileInfo[]>();

  readonly files = signal<FileInfo[]>([]);

  fileTypes = computed<FileTypes>(() => {
    for (const file of this.files()) {
      if (file.file?.type.includes('image')) {
        return 'image';
      }
      return 'other';
    }
    return 'other';
  });

  disableButtonForSendMsg$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  disableButtonForUploadFile$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  disableButton$: Observable<boolean> = combineLatest([
    this.disableButtonForSendMsg$$.asObservable(),
    this.disableButtonForUploadFile$$.asObservable(),
  ]).pipe(map(([sendMsg, uploadFile]) => sendMsg || uploadFile));

  isComposerDisabled = signal<boolean>(false);

  showChannelInfoPopover = false;

  selectedChatSourceId = signal<ChatSourceId | undefined>(undefined);

  private readonly currentConversationChannel$ = this.availableChannels$.pipe(
    map((availableChannels) => {
      return availableChannels?.preferredChannel;
    }),
  );

  readonly isOpenAIChannel$ = this.currentConversationChannel$.pipe(
    map((channel) => channel === ConversationChannel.CONVERSATION_CHANNEL_OPENAI),
  );

  readonly canAccessVoiceRecorder$ = this.currentConversationChannel$.pipe(
    map((channel) => {
      return channel === ConversationChannel.CONVERSATION_CHANNEL_INTERNAL;
    }),
  );

  readonly chatSourcesInfo$: Observable<ChatSourceInfo[]> = this.availableChannels$.pipe(
    switchMap((value) => {
      const channels = value.availableChannels ?? [];
      if (channels.length === 0) {
        return of([]);
      }
      const chatSourcesInfo = channels.map((channel) => {
        return this.getChatSourceInfo(channel).pipe(first());
      });
      return forkJoin(chatSourcesInfo);
    }),
  );

  readonly availableChatSources$: Observable<ChatSource[]> = this.chatSourcesInfo$.pipe(
    map((chatSourceInfo): ChatSource[] => {
      return chatSourceInfo.map((info) => {
        return mapConversationChannelToChatSource(info.channel, info.phoneNumber);
      });
    }),
    map((v) => v ?? new Array<ChatSource>()),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly popoverConfig$: Observable<PopoverConfig> = this.chatSourcesInfo$.pipe(
    map((results) => {
      if (results.length !== 1) {
        return <PopoverConfig>{
          title: '',
          contentFirst: '',
          contentSecond: '',
        };
      }
      const result = results[0];
      switch (result.channel) {
        case ConversationChannel.CONVERSATION_CHANNEL_SMS:
          return this.setSMSChannelPopoverInfo(result.phoneNumber);
        case ConversationChannel.CONVERSATION_CHANNEL_INTERNAL:
          return this.setInPlatformChannelPopoverInfo();
        case ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_BUSINESS_COMMUNICATIONS:
          return this.setGoogleBusinessCommunicationsPopoverInfo();
        default:
          return <PopoverConfig>{
            title: '',
            contentFirst: '',
            contentSecond: '',
          };
      }
    }),
  );

  readonly showPopover$: Observable<boolean> = combineLatest([this.isOpenAIChannel$, this.popoverConfig$]).pipe(
    map(([isOpenAIChannel, popoverConfig]) => {
      return !isOpenAIChannel && !!popoverConfig.title;
    }),
  );

  canAccessPaymentLink$: Observable<boolean>;

  TOOLTIP_BOTTOM_RIGHT = [{ ...PopoverPositions.TopRight }];
  showImpersonatedPopover = false;

  composerCollapsedActions: ComposerAction[] = [];

  showTemplates$ = this.inboxService.canAccessMessageTemplateSettingsCard$;

  constructor(
    private conversationService: ConversationService,
    private featureFlagService: FeatureFlagService,
    private paymentService: PaymentService,
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
    @Inject(ACCOUNT_GROUP_ID_TOKEN) readonly accountGroupId$: Observable<string>,
    private inboxService: InboxService,
    private inboxUploadService: InboxUploadService,
    public modal: MatDialog,
    private analyticsService: ProductAnalyticsService,
  ) {
    this.conversationDetail$.pipe(takeUntilDestroyed()).subscribe((conversationDetail) => {
      if (conversationDetail?.conversation?.conversationId) {
        this.inboxUploadService.conversationId$$.next(conversationDetail.conversation.conversationId);
      }
    });

    this.canAccessPaymentLink$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.featureFlagService.batchGetStatus(partnerId, '', ['inbox_payment_link']).pipe(
          take(1),
          shareReplay(1),
          map((res) => res['inbox_payment_link']),
        );
      }),
      withLatestFrom(this.accountGroupId$),
      switchMap(([canAccess, accountGroupId]) => {
        if (canAccess && this.inboxService.isBusinessApp) {
          // check if we have setup the retail merchant's stripe account
          return this.paymentService.getRetailProvider(accountGroupId).pipe(
            take(1),
            map((retailProvider) => !!retailProvider?.stripeConnectId),
          );
        }
        if (canAccess && this.inboxService.isPartnerCenter) {
          // check if user has access to Partner Admin views. this will hide the menu for salesperson-only role
          return this.inboxService.userIsPartnerAdmin$;
        }
        return of(false);
      }),
      take(1),
    );

    effect(() => this.setComposerDisabled(this.selectedChatSourceId()), { allowSignalWrites: true });
  }

  ngOnChanges(): void {
    this.setComposerDisabled(this.selectedChatSourceId());
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.inboxService.isBusinessApp) {
      this.composerCollapsedActions.push(<ComposerAction>{
        id: 'customervoice',
        action: 'INBOX.CHAT.INSERT_REVIEW_LINK',
        icon: 'star_outline',
        matMenu: (await this.inboxInsertReview?.getInserReviewMenu()) || null,
        visible: of(true),
      });
    }

    const canAccessPaymentLink = await firstValueFrom(this.canAccessPaymentLink$).catch(() => false);

    if (canAccessPaymentLink) {
      this.composerCollapsedActions.push(<ComposerAction>{
        id: 'insertpaymentlink',
        action: 'INBOX.REQUEST_PAYMENT.TITLE',
        icon: 'attach_money',
        onClick: () => this.inboxInsertPaymentLink?.openInsertLinkModal(),
        visible: of(true),
      });
    }

    const showTemplates = await firstValueFrom(this.showTemplates$).catch(() => false);

    if (showTemplates) {
      this.composerCollapsedActions.push(<ComposerAction>{
        id: 'inserttemplate',
        action: 'INBOX.MESSAGE_TEMPLATES.TITLE',
        icon: 'article',
        onClick: async () => {
          const trackingProps = await this.inboxService.buildTemplateTrackProperties();
          this.analyticsService.trackEvent('inbox', 'template', 'insert-template-button-click', 0, trackingProps);
          this.openInsertTemplateModal();
        },
        visible: of(true),
      });
    }
  }

  setComposerDisabled(selectedChatSourceId: ChatSourceId | undefined) {
    this.isComposerDisabled.set(
      this.isImpersonating || this.missingContactInformation || selectedChatSourceId === undefined,
    );
  }

  private getChatSourceInfo(ch: ConversationChannel): Observable<ChatSourceInfo> {
    const result: ChatSourceInfo = {
      channel: ch,
    };

    if (ch === ConversationChannel.CONVERSATION_CHANNEL_SMS) {
      // If it's multilocation Inbox, we need to get the current conversation's SMS number because conversations in multilocation have different phone numbers
      if (this.hostAppInterface.getAppOptions().is_multilocation) {
        return this.conversationService.currentConvoSMSNumber$.pipe(
          filter((resp) => !!resp),
          map((resp) => {
            result.phoneNumber = resp.phoneNumber ?? undefined;
            return result;
          }),
        );
        // If it's regular Inbox, we need to get the phone number of account group so that users still can send SMS in new message component
      } else {
        return this.conversationService.sendSMSNumber$.pipe(
          filter((phoneNumber) => phoneNumber !== null),
          map((phoneNumber) => {
            result.phoneNumber = phoneNumber ?? undefined;
            return result;
          }),
        );
      }
    }
    return of(result);
  }

  onSubmit(sentMessage: SentMessage): void {
    const text = sentMessage?.messageText;
    const attachments = this.getMessageMediaInfo(this.files());
    const channel = mapChatSourceIdToConversationChannel(sentMessage.sourceId!);

    const messageInfo: MessageInfo = {
      text: text,
      attachments: attachments,
      channel,
    };

    if ((text && text.trim() !== '') || messageInfo.attachments.length > 0) {
      this.sendMessage.emit(messageInfo);
      if (this.files().length > 0) {
        this.removeFile();
      }
    }
  }

  getMessageMediaInfo(files: FileInfo[]): MediaInterface[] {
    if (!files || files.length <= 0) {
      return [];
    }

    const messageMedia: MediaInterface[] = files.map((file) => {
      const { media_content_type, media_location_path, media_file_name, media_file_size } = file.resp.data;
      return {
        mediaContentType: media_content_type,
        mediaLocationPath: media_location_path,
        mediaFileName: media_file_name,
        fileSize: media_file_size,
      } as MediaInterface;
    });

    return messageMedia;
  }

  toggleChannelInfoPopover(): void {
    this.showChannelInfoPopover = !this.showChannelInfoPopover;
  }

  closeChannelInfoPopover(): void {
    this.showChannelInfoPopover = false;
  }

  showImpersonatedPopovers(): void {
    this.showImpersonatedPopover = true;
  }

  hideImpersonatedPopovers(): void {
    this.showImpersonatedPopover = false;
  }

  insertLink(reviewLink: string): void {
    this.accountGroupId$
      .pipe(
        switchMap((accountGroupId) => this.hostAppInterface.shortenLink(reviewLink, accountGroupId)),
        take(1),
      )
      .subscribe((shortenReviewLink) => {
        this.chatComposer?.insertDynamicContent(shortenReviewLink, 'curser');
      });
    this.chatComposer?.composerTextArea?.nativeElement?.focus();
  }

  setFile(file: FileInfo): void {
    this.files.set([file]);
    switch (file.status) {
      case FileUploadStatus.Queued:
      case FileUploadStatus.InProgress:
        this.disableButtonForUploadFile$$.next(true);
        break;
      case FileUploadStatus.Success:
      case FileUploadStatus.Fail:
      default:
        this.disableButtonForUploadFile$$.next(false);
        break;
    }
    this.fileChange.emit(this.files());
  }

  removeFile(): void {
    this.files.set(new Array<FileInfo>());
    //Clear file and queue and error
    this.inboxUploadService.clear();
    this.inboxUploadService.fileErrored$$.next(null);
    this.disableButtonForUploadFile$$.next(false);
    this.fileChange.emit(this.files());
  }

  setInPlatformChannelPopoverInfo(): PopoverConfig {
    return <PopoverConfig>{
      title: 'INBOX.INFO.PARTNER_PLATFORM_LABEL',
      contentFirst: 'INBOX.POPOVER.IN_PLATFORM_CHANNEL_MESSAGE_INFO',
      contentSecond: '',
    };
  }

  setGoogleBusinessCommunicationsPopoverInfo(): PopoverConfig {
    return <PopoverConfig>{
      title: 'INBOX.INFO.GOOGLE_BUSINESS_COMMUNICATIONS_LABEL',
      contentFirst: 'INBOX.POPOVER.GOOGLE_BUSINESS_COMMUNICATIONS_CHANNEL_MESSAGE_INFO',
      contentSecond: '',
    };
  }

  setSMSChannelPopoverInfo(phoneNumber?: string): PopoverConfig {
    const popoverConfig = <PopoverConfig>{
      title: '',
      contentFirst: '',
      contentSecond: '',
    };
    if (!phoneNumber) {
      popoverConfig.title = 'INBOX.POPOVER.BUSINESS_INITIATED_CONVERSATIONS';
      popoverConfig.contentFirst = 'INBOX.POPOVER.NO_PHONE_NUMBER_PROVISIONED';
      popoverConfig.contentSecond = '';
    }
    return popoverConfig;
  }

  clearText(): void {
    this.chatComposer?.clearMessageText();
  }

  async openInsertTemplateModal(): Promise<void> {
    const modalRef$ = this.modal.open(InboxInsertTemplateComponent, {
      width: '620px',
      autoFocus: false,
    });

    try {
      const template = await firstValueFrom(modalRef$.afterClosed());
      if (template) {
        this.chatComposer?.insertDynamicContent(template.content, 'curser');
        this.chatComposer?.composerTextArea?.nativeElement?.focus();
        const trackingProps = await this.inboxService.buildTemplateTrackProperties();
        this.analyticsService.trackEvent('inbox', 'template', 'insert-template-success', 0, {
          trackingProps,
          templateId: template.templateId,
        });
      }
    } catch (error) {
      console.error('error inserting template', error);
      this.snackbarService.openErrorSnack('INBOX.MESSAGE_TEMPLATES.MODAL.ERROR');
      return;
    }
  }

  get isMobile(): boolean {
    return isMobile();
  }
}

function mapChatSourceIdToConversationChannel(sourceId: ChatSourceId): ConversationChannel {
  switch (sourceId) {
    case 'sms':
      return ConversationChannel.CONVERSATION_CHANNEL_SMS;
    case 'facebook':
      return ConversationChannel.CONVERSATION_CHANNEL_FACEBOOK;
    case 'google':
      return ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_BUSINESS_COMMUNICATIONS;
    case 'webchat':
      return ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT;
    case 'openai':
      return ConversationChannel.CONVERSATION_CHANNEL_OPENAI;
    case 'platform':
      return ConversationChannel.CONVERSATION_CHANNEL_INTERNAL;
    case 'email':
      return ConversationChannel.CONVERSATION_CHANNEL_EMAIL;
    case 'instagram':
      return ConversationChannel.CONVERSATION_CHANNEL_INSTAGRAM;
    default:
      throw new Error(`Unknown chat source id: ${sourceId}`);
  }
}

function mapConversationChannelToChatSource(channel: ConversationChannel, phoneNumber?: string): ChatSource {
  switch (channel) {
    case ConversationChannel.CONVERSATION_CHANNEL_SMS:
      return { id: 'sms', sourceName: 'SMS', account: phoneNumber };
    case ConversationChannel.CONVERSATION_CHANNEL_FACEBOOK:
      return { id: 'facebook', sourceName: 'Facebook Messenger', shortSourceName: 'Facebook' };
    case ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_BUSINESS_COMMUNICATIONS:
      return { id: 'google', sourceName: 'Google Business Messages', shortSourceName: 'Google' };
    case ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT:
      return { id: 'webchat', sourceName: 'Web Chat' };
    case ConversationChannel.CONVERSATION_CHANNEL_OPENAI:
      return { id: 'openai', sourceName: '' };
    case ConversationChannel.CONVERSATION_CHANNEL_INTERNAL:
      return { id: 'platform', sourceName: 'Platform Chat', shortSourceName: 'Platform' };
    case ConversationChannel.CONVERSATION_CHANNEL_EMAIL:
      return { id: 'email', sourceName: 'Email' };
    case ConversationChannel.CONVERSATION_CHANNEL_INSTAGRAM:
      return { id: 'instagram', sourceName: 'Instagram Messenger' };
    default:
      throw new Error(`Unknown conversation channel: ${channel}`);
  }
}
